import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/test-system.css';
import './TestEntries.css';
import Navigation from '../components/Navigation';
import TestNav from '../components/tests/TestNav';
import EntryPackPicker, { useEntryPacks } from '../components/tests/EntryPackPicker';
import { getMyEntries, readApiError, startEntriesCheckout } from '../api/testSystem';
import { entriesLabel } from '../lib/entryPacks';
import { isAuthenticated } from '../utils/auth';
import { formatToLocalDateTime, getTimeZoneLabel } from '../utils/dateUtils';

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

/* The ledger, in a candidate's words. Every row is a movement of their own
   balance, so it reads as what happened rather than as a transaction type. */
const TRANSACTION_LABELS = {
    payment: 'Entries bought',
    enrolment: 'Spent on a cycle',
    cycle_cancellation: 'Returned — cycle cancelled',
    no_show_return: 'Returned — participation not confirmed',
    admin: 'Adjusted by the Appli team',
    dispute: 'Adjusted after a payment dispute',
};

const TYPE_LABELS = {
    grant: 'Added',
    spend: 'Spent',
    return: 'Returned',
    revoke: 'Removed',
};

const transactionLabel = (transaction) =>
    TRANSACTION_LABELS[transaction?.source_type] || TYPE_LABELS[transaction?.type] || 'Adjusted';

const TestEntries = () => {
    const navigate = useNavigate();

    const [balance, setBalance] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [pendingPack, setPendingPack] = useState('');
    const [actionError, setActionError] = useState('');
    const { loading: packsLoading, packs } = useEntryPacks();
    const [selectedPack, setSelectedPack] = useState('');
    // The smallest pack is picked until the student picks another.
    const chosenPack = packs.find(pack => pack.productId === selectedPack) || packs[0];
    /* Buying is on the page only once the server has said there is something to
       buy. While that is unknown, and when the answer is no — nothing on sale, or
       a packs route that failed — the page says nothing about buying at all. */
    const canBuy = !packsLoading && Boolean(chosenPack);

    const loadEntries = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            const data = await getMyEntries();
            setBalance(data?.entries_balance ?? 0);
            setTransactions(data?.transactions || []);
        } catch (e) {
            setLoadError(readApiError(e, 'Your entries could not be loaded.').message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadEntries();
    }, [navigate, loadEntries]);

    const handleBuy = async (productId) => {
        setPendingPack(productId);
        setActionError('');

        try {
            const data = await startEntriesCheckout(productId);
            if (data?.checkout_url) {
                // Stripe's page shows this pack, adds the tax and takes any discount
                // code; it cannot switch packs, so the choice is made here. Stay
                // busy through the redirect.
                window.location.href = data.checkout_url;
                return;
            }
            setActionError('Checkout could not be started. Please try again.');
        } catch (e) {
            setActionError(readApiError(e, 'Checkout could not be started.').message);
        }

        setPendingPack('');
    };

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <TestNav />

                    <header className="ts-head">
                        <div className="ts-head-main">
                            <h1 className="ts-title">Entries</h1>
                            <p className="ts-lede">
                                An entry is what enrols you on a test cycle — one entry, one cycle.
                                Entries never expire, and one comes back to you if its cycle is
                                cancelled.
                            </p>
                        </div>
                    </header>

                    {/* What you hold and what you can add to it on the left; the
                        record of every entry in and out beside it. */}
                    <div className="ts-entries-grid">
                        <div className="ts-entries-main">
                            <section className="ts-panel ts-balance-panel">
                                <div>
                                    <h2 className="ts-panel-title">Your balance</h2>
                                    <p className="ts-balance" data-testid="entries-balance">
                                        {loading ? '—' : balance ?? 0}
                                        <span className="ts-balance-unit">
                                            {balance === 1 ? 'entry' : 'entries'}
                                        </span>
                                    </p>
                                </div>
                                <Link className="ts-link" to="/tests">Browse open cycles</Link>
                            </section>

                            {loadError && (
                                <p className="ts-note ts-note--danger ts-standalone-note" role="alert" data-testid="entries-error">
                                    <AlertIcon />
                                    <span>{loadError}</span>
                                </p>
                            )}

                            {canBuy && (
                                <section className="ts-panel" data-testid="buy-entries">
                                    <h2 className="ts-panel-title">Buy entries</h2>

                                    {actionError && (
                                        <p className="ts-note ts-note--danger ts-pack-error" role="alert" data-testid="entries-action-error">
                                            <AlertIcon />
                                            <span>{actionError}</span>
                                        </p>
                                    )}

                                    <EntryPackPicker
                                        packs={packs}
                                        selected={chosenPack.productId}
                                        onSelect={setSelectedPack}
                                        disabled={!!pendingPack}
                                        name="entry-pack"
                                        label="Entry packs"
                                        showDescriptions
                                    />

                                    <div className="ts-pack-actions">
                                        <button
                                            className="ts-btn ts-btn--primary"
                                            onClick={() => handleBuy(chosenPack.productId)}
                                            disabled={!!pendingPack}
                                            data-testid="buy-pack"
                                        >
                                            {pendingPack && <span className="ts-spinner-sm" />}
                                            Buy {entriesLabel(chosenPack.entries)}
                                        </button>
                                        <p className="ts-pack-note">
                                            You pay on Stripe's secure page, which shows the total with tax
                                            before you pay. Discount codes are entered there too.
                                        </p>
                                    </div>
                                </section>
                            )}
                        </div>

                        <section className="ts-panel ts-entries-history">
                            <h2 className="ts-panel-title">History</h2>

                            {loading ? (
                                <p className="ts-history-empty">Loading…</p>
                            ) : transactions.length === 0 ? (
                                <p className="ts-history-empty" data-testid="entries-history-empty">
                                    Nothing yet. Every entry added, spent or returned shows up here.
                                </p>
                            ) : (
                                <ul className="ts-history" data-testid="entries-history">
                                    {transactions.map(transaction => (
                                        <li className="ts-history-row" key={transaction.txn_id}>
                                            <div className="ts-history-what">
                                                <span className="ts-history-label">
                                                    {transactionLabel(transaction)}
                                                </span>
                                                <span className="ts-history-when">
                                                    {formatToLocalDateTime(transaction.created_at)}{' '}
                                                    <span className="ts-history-zone">
                                                        {getTimeZoneLabel(transaction.created_at)}
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="ts-history-numbers">
                                                <span
                                                    className={`ts-history-delta${transaction.delta < 0 ? ' ts-history-delta--out' : ''}`}
                                                >
                                                    {transaction.delta > 0 ? `+${transaction.delta}` : transaction.delta}
                                                </span>
                                                <span className="ts-history-balance">
                                                    balance {transaction.balance_after}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TestEntries;
